export default {
  app: {
    title: 'Abhidhamma',
    description: 'Explore Abhidhamma through interactive, visual diagrams.'
  },
  nav: {
    home: 'Home',
    catalog: 'Diagram Catalog',
    about: 'About',
    contact: 'Contact'
  },
  home: {
    hero: {
      title: 'Explore Abhidhamma Through Visual Diagrams',
      subtitle: 'Gain deeper understanding of Mind, Mental Factors, and Matter through interactive, intuitive, and visual diagrams.',
      exploreButton: 'Explore Diagrams',
      learnMoreButton: 'Learn More',
      imageAlt: 'Abhidhamma Illustration'
    },
    features: {
      title: 'Key Features',
      subtitle: 'The Enlightenment App provides numerous features to help you easily access and deeply understand Abhidhamma.',
      feature1: {
        title: 'Interactive Diagrams',
        description: 'Interact with diagrams by zooming, panning, and clicking on nodes to view detailed information.'
      },
      feature2: {
        title: 'Advanced Search',
        description: 'Quickly search for concepts within diagrams and see highlighted results directly.'
      },
      feature3: {
        title: 'Cross-Platform',
        description: 'Enjoy a smooth experience on all devices: computers, tablets, and mobile phones.'
      },
      feature4: {
        title: 'Dark/Light Mode',
        description: 'Customize the interface with dark or light mode to protect your eyes and enhance user experience.'
      }
    },
    popularDiagrams: {
      title: 'Popular Diagrams',
      subtitle: 'Discover the most popular Abhidhamma diagrams.',
      viewAllButton: 'View All Diagrams'
    },
    aboutAbhidhamma: {
      title: 'About Abhidhamma',
      description1: 'Abhidhamma is a profound teaching in Buddhism, providing detailed analysis of mind, mental factors, and matter, helping to understand the nature of reality.',
      description2: 'The Enlightenment App simplifies these complex concepts through visual diagrams, making it easier for learners to access and apply them in daily life.',
      learnMoreButton: 'Learn More',
      imageAlt: 'Abhidhamma Illustration'
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'Have questions or feedback? Contact us through the following channels.',
      email: {
        title: 'Email',
        description: 'contact@giacngo.org'
      },
      social: {
        title: 'Social Media',
        description: 'Follow us on Facebook, Twitter, and Instagram.'
      },
      resources: {
        title: 'Resources',
        description: 'Access our library of documents and tutorial videos.'
      }
    }
  },
  catalog: {
    title: 'Abhidhamma Diagram Catalog',
    subtitle: 'Explore Abhidhamma diagrams categorized by topic and difficulty level.',
    search: 'Search diagrams...',
    categories: 'Categories',
    difficulty: 'Difficulty Level',
    tags: 'Tags',
    resetFilters: 'Reset Filters',
    diagrams: 'diagrams',
    sort: 'Sort:',
    sortOptions: {
      newest: 'Newest',
      oldest: 'Oldest',
      aToZ: 'A-Z',
      zToA: 'Z-A'
    },
    categoryOptions: {
      thangPhap: 'Abhidhamma',
      sutras: 'Sutras',
      meditation: 'Meditation',
      philosophy: 'Buddhist Philosophy',
      history: 'Buddhist History',
      practice: 'Practice'
    },
    difficultyOptions: {
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      advanced: 'Advanced'
    }
  },
  diagrams: {
    citta: {
      title: 'Mind Diagram (Citta)',
      shortDescription: 'Explore 89/121 types of consciousness and their relationships.'
    },
    cetasika: {
      title: 'Mental Factors Diagram (Cetasika)',
      shortDescription: 'Learn about 52 mental factors and how they combine with different types of consciousness.'
    },
    rupa: {
      title: 'Matter Diagram (Rūpa)',
      shortDescription: 'Discover 28 types of matter and how they are classified.'
    },
    fourNobleTruths: 'Four Noble Truths',
    eightfoldPath: 'Noble Eightfold Path',
    dependentOrigination: 'Dependent Origination',
    fiveAggregates: 'Five Aggregates',
    threeMarks: 'Three Marks of Existence',
    backToCatalog: 'Back to Catalog',
    viewModes: {
      mindmap: 'Mindmap',
      tree: 'Tree',
      radial: 'Radial'
    },
    controls: {
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      reset: 'Reset',
      search: 'Search in diagram...'
    },
    legend: 'Legend',
    details: 'Detailed Information'
  },
  diagram: {
    viewModes: {
      mindmap: 'Mindmap',
      tree: 'Tree',
      radial: 'Radial',
      complexMultiTier: 'Complex Multi-tier Layout'
    },
    resetView: 'Reset View',
    export: 'Export',
    searchPlaceholder: 'Search in diagram...',
    errorLoading: 'Error loading data',
    tryAgain: 'Please try again later',
    description: 'Description',
    details: 'Details',
    children: 'Children',
    nodeDetailsTip: 'You can still view node details while in flow highlight mode.',
    nodeTypes: {
      root: 'Root',
      kamavacara: 'Sensuous Sphere',
      rupavacara: 'Fine-Material Sphere',
      arupavacara: 'Immaterial Sphere',
      lokuttara: 'Supramundane',
      akusala: 'Unwholesome',
      ahetuka: 'Rootless',
      sobhana: 'Beautiful',
    },
    classificationBasis: 'Classification Basis',
    benefits: 'Benefits',
    subCategories: 'Subcategories',
    tabs: {
      diagram: 'Diagram',
      info: 'Information'
    },
    mindmap: 'Mindmap',
    tree: 'Tree',
    radial: 'Radial',
    horizontal: 'Horizontal Flow',
    reset: 'Reset',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit Fullscreen',
    legendAndTips: 'Legend and Tips',
    shortcuts: 'Shortcuts',
    tips: {
      title: 'Tips',
      zoom: 'Scroll to zoom in/out',
      pan: 'Drag to pan the diagram',
      click: 'Click on a node to view details',
      viewMode: 'Switch between different view modes',
      fullscreen: 'Use fullscreen mode for better viewing'
    },
    legend: {
      title: 'Legend',
      root: 'Mind (Citta)',
      rootDesc: 'Root node',
      kamavacara: 'Sensuous Sphere',
      kamavacaraDesc: 'Consciousness of the sensuous sphere',
      rupavacara: 'Fine-Material Sphere',
      rupavacaraDesc: 'Consciousness of the fine-material sphere',
      arupavacara: 'Immaterial Sphere',
      arupavacaraDesc: 'Consciousness of the immaterial sphere',
      lokuttara: 'Supramundane',
      lokuttaraDesc: 'Supramundane consciousness',
      akusala: 'Unwholesome',
      akusalaDesc: 'Unwholesome consciousness',
      ahetuka: 'Rootless',
      ahetukaDesc: 'Rootless consciousness',
      sobhana: 'Beautiful',
      sobhanaDesc: 'Beautiful consciousness',
      annasamana: 'Universal',
      annasamanaDesc: 'Universal mental factors',
      akusalaCetasikaDesc: 'Unwholesome mental factors',
      sobhanaCetasikaDesc: 'Beautiful mental factors',
      mahabhuta: 'Great Essentials',
      mahabhutaDesc: 'Great essential matter',
      upada: 'Derived',
      upadaDesc: 'Derived matter',
      pasada: 'Sensitivity',
      pasadaDesc: 'Sensitivity matter',
      gocara: 'Object',
      gocaraDesc: 'Object matter',
      bhava: 'Sex',
      bhavaDesc: 'Sex matter',
      hadaya: 'Heart-Base',
      hadayaDesc: 'Heart-base matter',
      jivita: 'Life',
      jivitaDesc: 'Life matter',
      ahara: 'Nutriment',
      aharaDesc: 'Nutriment matter',
      
      // Legend for Four Noble Truths
      fourNobleTruths: {
        dukkha: 'Suffering',
        dukkhaDesc: 'The truth of suffering',
        samudaya: 'Origin',
        samudayaDesc: 'The truth of the origin of suffering',
        nirodha: 'Cessation',
        nirodhaDesc: 'The truth of the cessation of suffering',
        magga: 'Path',
        maggaDesc: 'The truth of the path leading to the cessation of suffering'
      },
      
      // Legend for Noble Eightfold Path
      eightfoldPath: {
        rightView: 'Right View',
        rightViewDesc: 'Understanding the Four Noble Truths',
        rightIntention: 'Right Intention',
        rightIntentionDesc: 'Thoughts free from desire, ill-will, and cruelty',
        rightSpeech: 'Right Speech',
        rightSpeechDesc: 'Abstaining from lying, divisive speech, harsh speech, and idle chatter',
        rightAction: 'Right Action',
        rightActionDesc: 'Abstaining from killing, stealing, and sexual misconduct',
        rightLivelihood: 'Right Livelihood',
        rightLivelihoodDesc: 'Earning a living in a way that does not harm others',
        rightEffort: 'Right Effort',
        rightEffortDesc: 'Cultivating wholesome states and abandoning unwholesome states',
        rightMindfulness: 'Right Mindfulness',
        rightMindfulnessDesc: 'Being aware of body, feelings, mind, and phenomena',
        rightConcentration: 'Right Concentration',
        rightConcentrationDesc: 'Developing one-pointedness of mind'
      },
      
      // Legend for Dependent Origination
      dependentOrigination: {
        ignorance: 'Ignorance',
        ignoranceDesc: 'Not understanding the Four Noble Truths',
        formations: 'Formations',
        formationsDesc: 'Volitional activities',
        consciousness: 'Consciousness',
        consciousnessDesc: 'Awareness',
        nameForm: 'Name-and-Form',
        nameFormDesc: 'Mind and body',
        sixSenses: 'Six Sense Bases',
        sixSensesDesc: 'The six sense organs',
        contact: 'Contact',
        contactDesc: 'Meeting of sense organ, object, and consciousness',
        feeling: 'Feeling',
        feelingDesc: 'Pleasant, unpleasant, or neutral sensations',
        craving: 'Craving',
        cravingDesc: 'Desire for pleasure',
        clinging: 'Clinging',
        clingingDesc: 'Attachment',
        becoming: 'Becoming',
        becomingDesc: 'Process of coming into existence',
        birth: 'Birth',
        birthDesc: 'Rebirth',
        agingDeath: 'Aging and Death',
        agingDeathDesc: 'Old age and death'
      },
      
      // Legend for Five Aggregates
      fiveAggregates: {
        form: 'Form',
        formDesc: 'Physical matter, body',
        feeling: 'Feeling',
        feelingDesc: 'Sensations',
        perception: 'Perception',
        perceptionDesc: 'Recognition',
        mentalFormations: 'Mental Formations',
        mentalFormationsDesc: 'Volitional activities',
        consciousness: 'Consciousness',
        consciousnessDesc: 'Awareness'
      },
      
      // Legend for Three Marks of Existence
      threeMarks: {
        impermanence: 'Impermanence',
        impermanenceDesc: 'All phenomena are impermanent',
        suffering: 'Suffering',
        sufferingDesc: 'All phenomena are unsatisfactory',
        nonSelf: 'Non-Self',
        nonSelfDesc: 'All phenomena are without inherent self'
      }
    },
    diagram: 'Diagram',
    information: 'Information',
    introduction: 'Introduction',
    conclusion: 'Conclusion',
    category: 'Category',
    subcategory: 'Subcategory',
    item: 'Item',
    legend: 'Legend',
    tips: 'Tips',
    tipZoom: 'Scroll to zoom in/out',
    tipPan: 'Drag to pan the diagram',
    tipClick: 'Click on a node to view details',
    shortcutZoom: 'Zoom in/out',
    shortcutPan: 'Pan diagram',
    shortcutReset: 'Reset diagram',
    search: 'Search',
    enableHighlight: 'Enable flow highlight',
    disableHighlight: 'Disable flow highlight',
    hideAllEdges: 'Hide all edges',
    showAllEdges: 'Show all edges',
    exportPdf: 'Export PDF',
    exporting: 'Exporting PDF...',
    exportError: 'Error exporting PDF',
    content: 'Content',
    terms: 'Terms',
    examples: 'Examples',
    references: 'References',
    relatedConcepts: 'Related Concepts',
    "Mind Map": "Mind Map",
    "Tree Layout": "Tree Layout",
    "Radial Layout": "Radial Layout",
    "Horizontal Layout": "Horizontal Layout"
  },
  lessons: {
    title: 'Buddhist Lessons',
    subtitle: 'Explore Buddhist lessons categorized by proficiency level.',
    levels: {
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      advanced: 'Advanced'
    },
    readMore: 'Read More',
    relatedDiagrams: 'Related Diagrams',
    viewLesson: 'View Lesson'
  },
  footer: {
    links: 'Links',
    mainDiagrams: 'Main Diagrams',
    connect: 'Connect',
    copyright: 'All rights reserved.'
  },
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    notFound: 'Not Found',
    notFoundMessage: 'The page you are looking for does not exist.',
    backToHome: 'Back to Home',
    search: 'Search',
    filter_by_tags: 'Filter by tags',
    clear_all: 'Clear all',
    explore: 'Explore',
    back_to_list: 'Back to list'
  },
  collapse_info: 'Collapse Information',
  expand_info: 'Expand Information',
  hide_info: 'Hide Information',
  show_info: 'Show Information',
  search_in_diagram: 'Search in diagram...',
  difficulty: {
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced'
  },
  ai: {
    chat_page_title: 'Chat with Buddhist AI',
    chat_widget_title: 'Zen Master AI',
    type_message: 'Type your message...',
    send: 'Send',
    clear_conversation: 'Clear Chat History',
    open_chat: 'Open Chat',
    close_chat: 'Close Chat',
    rate_limit_message: 'You have reached the maximum number of messages for this hour. Please try again later.',
    rate_limit_exceeded: 'Message limit reached for this hour. Please try again later.',
    cooldown_message: 'Please wait {{seconds}} seconds before sending another message.',
    please_wait: 'Please wait {{seconds}} seconds before sending another message.',
    fullpage_mode: 'Full Page',
    widget_mode: 'Widget Mode',
    show_widget: 'Show Chat Widget',
    hide_widget: 'Hide Chat Widget',
    widget_description: 'The chat widget allows you to interact with the Zen Master AI while browsing other pages.',
    chat_about_this: 'Ask about this',
    chat_intro: 'Have questions about "{{contextName}}"? Ask our Zen Master AI!',
    chat_about_diagram: 'Chat about diagram:',
    chat_about_lesson: 'Chat about lesson:'
  },
  quizGame: {
    loading: 'Loading...',
    overview: 'Overview',
    leaderboard: 'Leaderboard',
    about: 'About',
    tags: 'Tags',
    stats: 'Stats',
    plays: 'Plays',
    created: 'Created',
    noDescription: 'No description',
    noTags: 'No tags',
    difficulty: 'Difficulty',
    difficultyBeginner: 'Beginner',
    difficultyIntermediate: 'Intermediate',
    difficultyAdvanced: 'Advanced',
    estimatedTime: 'Estimated time',
    questions: 'Questions',
    minutes: 'minutes',
    seconds: 'seconds',
    startGame: 'Start',
    continueGame: 'Continue',
    share: 'Share',
    shareText: 'Challenge your knowledge with {{title}} quiz game',
    completed: 'Completed!',
    completedDescription: 'You have completed the game. Here are your results:',
    finalScore: 'Final Score',
    timeElapsed: 'Time Elapsed',
    backToQuizzes: 'Back to quizzes',
    viewLeaderboard: 'View leaderboard',
    exitGame: 'Exit',
    noLeaderboardEntries: 'No players in leaderboard yet',
    anonymousUser: 'Anonymous player',
    gameNotFound: 'Game not found',
    gameNotFoundDescription: 'The game you are looking for does not exist or has been deleted',
    startToPlay: 'Start playing',
    title: 'Buddhist Quiz Games',
    subtitle: 'Test your Buddhist knowledge with fun quiz games',
    tabs: {
      explore: 'Explore',
      myGames: 'My Games',
      leaderboards: 'Leaderboards'
    },
    searchPlaceholder: 'Search games...',
    clearTags: 'Clear filters',
    leaderboardsComingSoon: 'Leaderboards coming soon',
    types: {
      multipleChoice: 'Multiple Choice'
    },
    public: 'Public',
    noLeaderboard: 'No leaderboard',
    play: 'Play'
  }
}
